import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useGuestGate } from "../lib/useGuestGate";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Trash2,
  Pencil,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { toast } from "sonner";

const WAVE_OPTIONS = ["1", "2", "3", "4", "5a", "5b", "5c", "5d", "5e"];

const EMPTY_FORM = {
  ticker: "",
  trend: "uptrend",
  day_wave: "1",
  week_wave: "1",
  month_wave: "1",
  day_trend: "uptrend",
  week_trend: "uptrend",
  month_trend: "uptrend",
  current_price: "",
  wave_start_price: "",
  recent_extreme_price: "",
  target: "",
  counter_market_structures: "0",
  wave_start_date: "",
  recent_extreme_date: "",
};

const money = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00");
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
};

function TrendToggle({ value, onChange, testid, size = "md" }) {
  const isUp = value === "uptrend";
  const isSm = size === "sm";
  const pad = isSm ? "px-2 py-1" : "px-3 py-1.5";
  const iconSize = isSm ? "w-3 h-3" : "w-3.5 h-3.5";
  const txt = isSm ? "text-[10px]" : "text-xs";
  return (
    <div
      className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5"
      role="group"
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => onChange("uptrend")}
        className={`inline-flex items-center gap-1 ${pad} rounded ${txt} font-semibold tracking-wide transition-colors ${
          isUp ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
        }`}
        data-testid={`${testid}-uptrend`}
        aria-pressed={isUp}
      >
        <TrendingUp className={iconSize} strokeWidth={2.25} />
        {!isSm && "Uptrend"}
      </button>
      <button
        type="button"
        onClick={() => onChange("downtrend")}
        className={`inline-flex items-center gap-1 ${pad} rounded ${txt} font-semibold tracking-wide transition-colors ${
          !isUp ? "bg-white text-red-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
        }`}
        data-testid={`${testid}-downtrend`}
        aria-pressed={!isUp}
      >
        <TrendingDown className={iconSize} strokeWidth={2.25} />
        {!isSm && "Downtrend"}
      </button>
    </div>
  );
}

function WaveTrendCell({ wave, trend }) {
  const isUp = trend === "uptrend";
  const Icon = isUp ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono tabular-nums text-sm font-semibold ${
        isUp ? "text-emerald-700" : "text-red-600"
      }`}
    >
      <span>{wave}</span>
      <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
    </span>
  );
}

function WaveSelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1.5 font-mono tabular-nums" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {WAVE_OPTIONS.map((w) => (
          <SelectItem
            key={w}
            value={w}
            className="font-mono tabular-nums"
            data-testid={`${testid}-option-${w}`}
          >
            {w}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Field({ label, htmlFor, children, suffix }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <Label htmlFor={htmlFor} className="text-xs tracking-[0.15em] uppercase text-slate-600">
          {label}
        </Label>
        {suffix && <span className="text-xs text-slate-400">{suffix}</span>}
      </div>
      {children}
    </div>
  );
}

export default function MarketDetailsPage() {
  const { gate } = useGuestGate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [mode, setMode] = useState(null); // "create" | "edit" | null
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/market-indices");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load indices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    if (gate("Adding an index")) return;
    setEditingId(null);
    setForm(EMPTY_FORM);
    setMode("create");
  };

  const openEdit = (it) => {
    if (gate("Editing an index")) return;
    setEditingId(it.id);
    setForm({
      ticker: it.ticker,
      trend: it.trend,
      day_wave: it.day_wave,
      week_wave: it.week_wave,
      month_wave: it.month_wave,
      day_trend: it.day_trend || it.trend,
      week_trend: it.week_trend || it.trend,
      month_trend: it.month_trend || it.trend,
      current_price: String(it.current_price),
      wave_start_price: String(it.wave_start_price),
      recent_extreme_price: String(it.recent_extreme_price),
      target: String(it.target),
      counter_market_structures: String(it.counter_market_structures ?? 0),
      wave_start_date: it.wave_start_date,
      recent_extreme_date: it.recent_extreme_date,
    });
    setMode("edit");
  };

  const closeDialog = () => {
    setMode(null);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ticker.trim()) return;
    setSubmitting(true);
    const body = {
      ticker: form.ticker.trim().toUpperCase(),
      trend: form.day_trend,
      day_wave: form.day_wave,
      week_wave: form.week_wave,
      month_wave: form.month_wave,
      day_trend: form.day_trend,
      week_trend: form.week_trend,
      month_trend: form.month_trend,
      current_price: Number(form.current_price),
      wave_start_price: Number(form.wave_start_price),
      recent_extreme_price: Number(form.recent_extreme_price),
      target: Number(form.target),
      counter_market_structures: Number(form.counter_market_structures || 0),
      wave_start_date: form.wave_start_date,
      recent_extreme_date: form.recent_extreme_date,
    };
    try {
      if (mode === "edit" && editingId) {
        const { data } = await api.put(`/market-indices/${editingId}`, body);
        setItems((prev) => prev.map((it) => (it.id === editingId ? data : it)));
        toast.success(`${data.ticker} updated`);
      } else {
        const { data } = await api.post("/market-indices", body);
        setItems((prev) => [data, ...prev]);
        toast.success(`${data.ticker} added`);
      }
      closeDialog();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          (mode === "edit" ? "Failed to update index" : "Failed to add index")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, ticker) => {
    if (gate("Removing an index")) return;
    try {
      await api.delete(`/market-indices/${id}`);
      setItems((prev) => prev.filter((s) => s.id !== id));
      toast.success(`${ticker} removed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove index");
    } finally {
      setConfirmTarget(null);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isUp = form.day_trend === "uptrend";
  const extremeLabel = isUp ? "Most Recent Top" : "Most Recent Bottom";

  return (
    <div className="space-y-8" data-testid="market-details-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <p className="text-slate-600 text-sm leading-relaxed max-w-xl">
          Record the state of the major indices — trend, wave structure across
          timeframes, key price levels and dates. Used to power the Market
          Conditions view.
        </p>
        <Button
          onClick={openCreate}
          className="bg-slate-900 hover:bg-slate-800 text-white gap-2"
          data-testid="add-index-button"
        >
          <Plus className="w-4 h-4" />
          Add Index
        </Button>
      </div>

      <Dialog open={mode !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="index-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {mode === "edit" ? "Edit Index" : "Add Index"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Ticker" htmlFor="mi-ticker">
              <Input
                id="mi-ticker"
                required
                value={form.ticker}
                onChange={(e) => setForm((f) => ({ ...f, ticker: e.target.value.toUpperCase() }))}
                placeholder="SPX"
                maxLength={15}
                className="mt-1.5 font-mono uppercase"
                data-testid="mi-ticker-input"
              />
            </Field>

            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-slate-600">
                Waves &amp; per-timeframe trend
              </Label>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { key: "day", label: "Day" },
                  { key: "week", label: "Week" },
                  { key: "month", label: "Month" },
                ].map(({ key, label }) => (
                  <div
                    key={key}
                    className="rounded-md border border-slate-200 bg-slate-50/50 p-3 space-y-2"
                    data-testid={`mi-${key}-block`}
                  >
                    <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-500">
                      {label}
                    </div>
                    <WaveSelect
                      value={form[`${key}_wave`]}
                      onChange={(v) => setForm((f) => ({ ...f, [`${key}_wave`]: v }))}
                      testid={`mi-${key}-wave`}
                    />
                    <TrendToggle
                      value={form[`${key}_trend`]}
                      onChange={(v) => setForm((f) => ({ ...f, [`${key}_trend`]: v }))}
                      testid={`mi-${key}-trend`}
                      size="sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Current Price" htmlFor="mi-current" suffix="$">
                <Input
                  id="mi-current"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={0}
                  required
                  value={form.current_price}
                  onChange={set("current_price")}
                  className="mt-1.5 font-mono tabular-nums"
                  placeholder="5000.00"
                  data-testid="mi-current-input"
                />
              </Field>
              <Field label="Target" htmlFor="mi-target" suffix="$">
                <Input
                  id="mi-target"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={0}
                  required
                  value={form.target}
                  onChange={set("target")}
                  className="mt-1.5 font-mono tabular-nums"
                  placeholder="5500.00"
                  data-testid="mi-target-input"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Wave Start Price" htmlFor="mi-start-price" suffix="$">
                <Input
                  id="mi-start-price"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={0}
                  required
                  value={form.wave_start_price}
                  onChange={set("wave_start_price")}
                  className="mt-1.5 font-mono tabular-nums"
                  placeholder="4200.00"
                  data-testid="mi-start-price-input"
                />
              </Field>
              <Field label="Wave Start Date" htmlFor="mi-start-date">
                <Input
                  id="mi-start-date"
                  type="date"
                  required
                  value={form.wave_start_date}
                  onChange={set("wave_start_date")}
                  className="mt-1.5"
                  data-testid="mi-start-date-input"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={`${extremeLabel} Price`} htmlFor="mi-extreme-price" suffix="$">
                <Input
                  id="mi-extreme-price"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={0}
                  required
                  value={form.recent_extreme_price}
                  onChange={set("recent_extreme_price")}
                  className="mt-1.5 font-mono tabular-nums"
                  placeholder="5100.00"
                  data-testid="mi-extreme-price-input"
                />
              </Field>
              <Field label={`${extremeLabel} Date`} htmlFor="mi-extreme-date">
                <Input
                  id="mi-extreme-date"
                  type="date"
                  required
                  value={form.recent_extreme_date}
                  onChange={set("recent_extreme_date")}
                  className="mt-1.5"
                  data-testid="mi-extreme-date-input"
                />
              </Field>
            </div>

            <Field label="Counter Market Structures" htmlFor="mi-cms">
              <Input
                id="mi-cms"
                type="number"
                inputMode="numeric"
                step={1}
                min={0}
                required
                value={form.counter_market_structures}
                onChange={set("counter_market_structures")}
                className="mt-1.5 font-mono tabular-nums"
                placeholder="0"
                data-testid="mi-cms-input"
              />
            </Field>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeDialog}
                data-testid="cancel-index-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="submit-index-button"
              >
                {submitting ? "Saving..." : mode === "edit" ? "Save Changes" : "Add Index"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div
        className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm"
        data-testid="indices-table"
      >
        <table className="w-full min-w-[1080px] table-auto">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Ticker</th>
              <th className="text-center py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Day</th>
              <th className="text-center py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Week</th>
              <th className="text-center py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Month</th>
              <th className="text-right py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Current</th>
              <th className="text-right py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Wave Start</th>
              <th className="text-right py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Top / Bottom</th>
              <th className="text-right py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Target</th>
              <th className="text-center py-4 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500" title="Counter Market Structures">CMS</th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[120px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="py-12 text-center text-sm text-slate-500">
                  Loading indices...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-16 text-center" data-testid="empty-indices">
                  <div className="text-sm text-slate-500">No indices recorded yet.</div>
                  <div className="text-xs text-slate-400 mt-1">Click "Add Index" to start tracking one.</div>
                </td>
              </tr>
            ) : (
              items.map((it) => {
                const up = (it.day_trend || it.trend) === "uptrend";
                return (
                  <tr
                    key={it.id}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                    data-testid={`index-row-${it.ticker}`}
                  >
                    <td className="py-4 px-6">
                      <span className="font-mono font-semibold text-slate-900 text-base tracking-wide">
                        {it.ticker}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <WaveTrendCell wave={it.day_wave} trend={it.day_trend || it.trend} />
                    </td>
                    <td className="py-4 px-4 text-center">
                      <WaveTrendCell wave={it.week_wave} trend={it.week_trend || it.trend} />
                    </td>
                    <td className="py-4 px-4 text-center">
                      <WaveTrendCell wave={it.month_wave} trend={it.month_trend || it.trend} />
                    </td>
                    <td className="py-4 px-4 text-right font-mono tabular-nums text-sm text-slate-900 font-semibold">
                      {money(it.current_price)}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="font-mono tabular-nums text-sm text-slate-700">{money(it.wave_start_price)}</div>
                      <div className="text-[11px] text-slate-400">{fmtDate(it.wave_start_date)}</div>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className={`font-mono tabular-nums text-sm font-medium ${up ? "text-emerald-700" : "text-red-600"}`}>
                        {money(it.recent_extreme_price)}
                      </div>
                      <div className="text-[11px] text-slate-400">{fmtDate(it.recent_extreme_date)}</div>
                    </td>
                    <td className="py-4 px-4 text-right font-mono tabular-nums text-sm text-slate-700">
                      {money(it.target)}
                    </td>
                    <td className="py-4 px-3 text-center font-mono tabular-nums text-sm text-slate-700">
                      {it.counter_market_structures ?? 0}
                    </td>
                    <td className="py-4 px-6 text-right w-[120px]">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => openEdit(it)}
                          aria-label={`Edit ${it.ticker}`}
                          title={`Edit ${it.ticker}`}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition-colors"
                          data-testid={`edit-index-${it.id}`}
                        >
                          <Pencil className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                        <button
                          type="button"
                          onClick={() => { if (gate("Removing an index")) return; setConfirmTarget({ id: it.id, ticker: it.ticker }); }}
                          aria-label={`Remove ${it.ticker}`}
                          title={`Remove ${it.ticker}`}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                          data-testid={`delete-index-${it.id}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={confirmTarget !== null} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <AlertDialogContent data-testid="delete-index-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove index?</AlertDialogTitle>
            <AlertDialogDescription>
              Stop tracking <span className="font-mono text-slate-800">{confirmTarget?.ticker}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-index">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmTarget && handleDelete(confirmTarget.id, confirmTarget.ticker)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-index"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
